import { getIn, type Decorator, type FormApi } from 'final-form'
import type { GetInputs, FindInput, FocusableInput } from './types'

const findInput: FindInput = (inputs: FocusableInput[], errors: {}) =>
  inputs.find((input) => input.name && getIn(errors, input.name))

const isFocusableInput = (input: any) =>
  !!(input && typeof input.focus === 'function')

const getAllInputs: GetInputs = () => {
  if (typeof document === 'undefined') {
    return []
  }
  return Array.prototype.slice
    .call(document.forms)
    .reduce(
      (accumulator, form) =>
        accumulator.concat(
          Array.prototype.slice.call(form).filter(isFocusableInput),
        ),
      [],
    )
}

const createDecorator = (): Decorator => (form: FormApi) => {
  const focusOnFirstError = (errors: Object) => {
    const firstInput = findInput(getAllInputs(), errors)
    if (firstInput) {
      firstInput.focus()
    }
  }
  // Save original submit function
  const originalSubmit = form.submit

  // Subscribe to errors, and keep a local copy of them
  let state: { errors?: Object; submitErrors?: Object } = {}
  const unsubscribe = form.subscribe(
    (nextState) => {
      state = nextState
    },
    { errors: true, submitErrors: true },
  )

  // What to do after submit
  const afterSubmit = () => {
    const { errors, submitErrors } = state
    if (errors && Object.keys(errors).length) {
      focusOnFirstError(errors)
    } else if (submitErrors && Object.keys(submitErrors).length) {
      focusOnFirstError(submitErrors)
    }
  }

  // Rewrite submit function
  form.submit = () => {
    const result = originalSubmit.call(form)
    if (result && typeof result.then === 'function') {
      // async
      result.then(afterSubmit, {})
    } else {
      // sync
      afterSubmit()
    }
    return result
  }

  return () => {
    unsubscribe()
    form.submit = originalSubmit
  }
}

export default createDecorator
